<template>
    <div class="section-wrapper">
        <!-- Destination Section -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'group'"
        :title="t('transfers.nacional.step1.dividers.destination')"/>
        <!-- Same Holder switch -->
        <switch-text
        v-if="tabsList[0].selected"
        :icon="'document-people'"
        :text="t('transfers.nacional.genericLabels.sameHolder')"
        :isActive="formData.sendToSameHolder"
		@clicked="switchClickHandler"/>
        <!-- Not Same Holder logic -->
        <div
        v-if="!formData.sendToSameHolder"
        class="generic-destination-container"
        :class="{'mobyle-styles': getMobileAppState}">
            <div class="generic-destination-wrapper">
                <!-- Account Number/IBAN/PhoneNumber Input  -->
                <input-generic
                id="genericDestination"
                :key="tabsList[1].selected"
                :params="genericDestinationParams"
                :inputValue="formData.genericDestination"
                :rightButtonIcon="getMobileAppState && props.tabsList[2].selected ? 'contact' : null"
                @onBlur="props.tabsList[2].selected ? phoneNumberInputValidator() : genericDestinationValidation()"
                @input="[genericDestinationInputHandler($event), emit('resetBeneficiaryInfoError')]"
                @rightButtonClicked="emit('changeBeneficiary')"/>
                <!-- QR Code -->
                <button-tertiary
                v-if="getMobileAppState && props.tabsList[2].selected"
                class="qr-code-button"
                :style="{'margin-top': marginValidator ? '24px' : '0'}"
                :icon="'qr-code'"
                @clicked="emit('openQrCodeModal')"/>
            </div>
            <!-- Reuse Beneficary -->
            <button-tertiary
            class="reuse-beneficiary-button"
            buttonSize="small"
            :label="t('transfers.nacional.step1.reuseBeneficiary')"
            :icon="'person-reuse'"
            :style="{'margin-top': marginValidator && !getMobileAppState ? '24px' : (sameBankDestinationError ? '-32px' : '0')}"
            @clicked="emit('openReuseBeneficiaryModal')"/>
        </div>
        <!-- Account Dropdown same holder -->
        <dropdown-accounts
        v-if="tabsList[0].selected && formData.sendToSameHolder && destinationAccountsList.length"
        id="genericDestination"
        :params="sameHolderAccountParams"
        :options="destinationAccountsList"
        :selectedOption="destinationAccountsList.find(obj => obj.accountNumber === formData.genericDestination).id"
        @clicked="sameHolderClickHandler($event)"/>
        <!-- No Accounts Warning -->
        <infobox-generic
        v-if="formData.sendToSameHolder && !destinationAccountsList.length"
        state="negative"
        :label="t('transfers.nacional.step1.noDestinationAccountsMessage')"/>
        <!-- Beneficiary Name Output -->
        <infobox-generic
        v-if="formData.beneficiaryName && !formData.sendToSameHolder && validatedBeneficiary && !tabsList[1].selected"
        state="positive"
        :label="t('transfers.nacional.step1.accountHolder', {name: formData.beneficiaryName})"/>
        <!-- Beneficiary name input -->
        <input-generic
        v-if="tabsList[1].selected"
        id="beneficiaryName"
        :params="beneficiaryNameInputParams"
        :inputValue="formData.beneficiaryName"
        @onBlur="beneficiaryNameValidation"
        @input="[emit('updateFormData', { field: 'beneficiaryName', inputValue: $event }), clearFieldError('beneficiaryName')]" />
        <!-- Address input -->
        <input-generic
        v-if="tabsList[1].selected"
        id="address"
        :params="addressInputParams"
        :inputValue="formData.address"
        @input="[emit('updateFormData', { field: 'address', inputValue: $event }), clearFieldError('address')]"/>
        <!-- No Wallet Information -->
        <infobox-generic
        v-if="beneficiaryInfo && !!beneficiaryInfo.description && !!beneficiaryInfo.subDescription && props.tabsList[2].selected"
        state="warning"
        :label="beneficiaryInfo.description"
        :content="beneficiaryInfo.subDescription"/>
</div>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import SwitchText from '@/components/generic/switch/Text'
import InputGeneric from '@/components/generic/inputs/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import DropdownAccounts from '@/components/generic/dropdowns/accounts/Account'
import DividerGeneric from '@/components/generic/dividers/Generic'

/* Vue && Assets */
import { ref, computed, watch } from 'vue'
import { ACCOUNTS_NIB_IBAN, PHONE_NUMBER, OURMASK_IBAN_AO } from '@/assets/constants/masks'
import { accountNumberFiller } from '@/assets/scripts/strings'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { noSpecialCharTransfers, removeAccents, noSpecialChar, phoneNumberValidator } from '@/assets/scripts/validators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getAccountsBalances } = mapGetters(moduleMapper.TRANSFERS_NACIONAL)
const { getMobileAppState } = mapGetters()

/** Props
@formData {Object} - Object with form data
@tabsList {Array} - Transfer Type Tab List
@validatedBeneficiary {Boolean} - Boolean controller show validated beneficiary inputs
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@beneficiaryInfo { Object } - object with data from searched beneficiary msisdn
@beneficiaryInfoError {Object} - Object with beneficiary info request error
@clearForm {Boolean} - Boolean controller to clear form and errors
*/
const props = defineProps({
    formData: Object,
    tabsList: Array,
    validatedBeneficiary: Boolean,
    beneficiaryInfoError: Object,
    beneficiaryInfo: Object,
    showInputsErrors: Boolean,
    clearForm: Boolean
})

const emit = defineEmits([
    'updateFormData',
    'openReuseBeneficiaryModal',
    'openQrCodeModal',
    'requestBeneficiaryInfo',
    'requestBeneficiaryName',
    'changeBeneficiary',
    'resetBeneficiaryInfoError',
    'resetShowInputsErrors',
    'formIsCleared',
    'scrollTo'
])

const marginValidator = computed(() => {
    return !genericDestinationParams.value.errorState
})

/*************************************************
*                                                *
*              Generic Destination               *
*                                                *
*************************************************/
const sameBankDestinationError = ref(false)

const destinationLabel = computed(() => {
    if (props.tabsList[0].selected) {
        return t('transfers.nacional.genericLabels.genericDestination')
    } else if (props.tabsList[1].selected) {
          return t('transfers.nacional.genericLabels.genericDestinationIban')
    } else return t('transfers.nacional.genericLabels.phoneNumber')
})

const destinationPlaceholder = computed(() => {
    if (props.tabsList[0].selected) {
        return t('transfers.nacional.step1.placeholders.genericDestination')
    } else if (props.tabsList[1].selected) {
          return t('transfers.nacional.step1.placeholders.genericDestinationIban')
    } else return t('transfers.nacional.step1.placeholders.phoneNumber')
})

const destinationMask = computed(() => {
    if (props.tabsList[0].selected) {
        return ACCOUNTS_NIB_IBAN
    } else if (props.tabsList[1].selected) {
        return null
    } else return PHONE_NUMBER
})

const destinationOurMask = computed(() => {
    if (props.tabsList[1].selected) return OURMASK_IBAN_AO
    return null
})

const genericDestinationParams = ref({
    labelText: destinationLabel,
    placeholder: destinationPlaceholder,
    mask: destinationMask,
    ourMask: destinationOurMask,
    maxLength: props.tabsList[1].selected ? 31 : null,
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const genericDestinationInputHandler = event => {
    emit('updateFormData', { field: 'genericDestination', inputValue: event })
    clearFieldError('genericDestination')
    sameBankDestinationError.value = false
}

const genericDestinationValidation = () => {
    if (!props.formData.genericDestination) return

    const isIBAN = props.formData.genericDestination.includes('AO06')

    if (props.tabsList[0].selected) emit('requestBeneficiaryInfo')

    if (!isIBAN && props.formData.genericDestination.length < 14 && props.formData.genericDestination.length > 0) {
        emit('updateFormData', { field: 'genericDestination', inputValue: accountNumberFiller(props.formData.genericDestination) })
    }
}

const phoneNumberInputValidator = () => {
    if (!props.formData.genericDestination.length) return

    if (!!props.formData.genericDestination.length && !phoneNumberValidator(props.formData.genericDestination)) {
        genericDestinationParams.value.errorState = true

        genericDestinationParams.value.errorMessage = t('phoneLengthError')

        emit('scrollTo', getElementOffsetTopPosition('genericDestination'))
    } else emit('requestBeneficiaryName')
}
/*************************************************
*                                                *
*            SameHolder Account Data             *
*                                                *
*************************************************/

const sameHolderAccountParams = ref({
    labelText: t('transfers.nacional.genericLabels.account'),
    mandatory: true
})

const destinationAccountsList = computed(() => {
    if (!props.formData.originAccount) return []
    const availableAccounts = getAccountsBalances.value.filter(obj => obj.id !== props.formData.originAccount)
    const selectedAccount = getAccountsBalances.value.find(obj => obj.id === props.formData.originAccount)

    return availableAccounts.filter(obj => obj.currency === selectedAccount.currency)
})

const sameHolderClickHandler = event => {
    const selectedAccount = destinationAccountsList.value.find(obj => obj.id === event).accountNumber
    const beneficiaryName = selectedAccount.firstHolderName

    emit('updateFormData', { field: 'genericDestination', inputValue: selectedAccount })
    emit('updateFormData', { field: 'beneficiaryName', inputValue: beneficiaryName })
}

/*
for SAMEBANK transfers: watches the selected origin account and if it's the same as the selected option on
destination account dropdown, the selectedDestinationAccount will be to the first
entry in destinationAccountsList
*/
watch(() => props.formData.originAccount, newVal => {
    if (!props.formData.sendToSameHolder) return

    const account = getAccountsBalances.value.find(obj => obj.accountNumber === props.formData.genericDestination).id
    if (newVal === account) {
        const selectedAccount = destinationAccountsList.value[0]
        const beneficiaryName = selectedAccount.firstHolderName

        emit('updateFormData', { field: 'genericDestination', inputValue: selectedAccount.accountNumber })
        emit('updateFormData', { field: 'beneficiaryName', inputValue: beneficiaryName })
    }
})
/*************************************************
*                                                *
*                Beneficiary Name                *
*                                                *
*************************************************/

const beneficiaryNameInputParams = ref({
    labelText: t('transfers.nacional.genericLabels.beneficaryName'),
    placeholder: t('transfers.nacional.step1.placeholders.beneficaryName'),
    maxLength: 80,
    ourMask: {
        validatorFn: value => { return noSpecialChar(value) },
        parseInputText: value => { return removeAccents(value) }
    },
	mandatory: true,
    errorState: false,
    errorMessage: null,
    bottomMessage: t('transfers.nacional.step1.beneficiaryNameNote')
})

const beneficiaryNameValidation = () => {
    if (props.formData.beneficiaryName.length && props.formData.beneficiaryName.length < 5) {
        beneficiaryNameInputParams.value.errorState = true
        beneficiaryNameInputParams.value.errorMessage = t('transfers.nacional.step1.invalidBeneficiaryName')
    }
}

/*************************************************
*                                                *
*                   Address                      *
*                                                *
*************************************************/

const addressInputParams = ref({
	labelText: t('transfers.nacional.genericLabels.address'),
    placeholder: t('transfers.nacional.step1.placeholders.address'),
	mandatory: true,
	errorState: false,
    errorMessage: null,
    maxLength: 70,
    ourMask: {
        validatorFn: value => { return noSpecialCharTransfers(value) },
        parseInputText: value => { return removeAccents(value) }
    }
})

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/

const switchClickHandler = event => {
    if (!props.formData.sendToSameHolder && destinationAccountsList.value.length) {
        emit('updateFormData', { field: 'genericDestination', inputValue: destinationAccountsList.value[0].accountNumber })
        emit('updateFormData', { field: 'beneficiaryName', inputValue: destinationAccountsList.value[0].firstHolderName })
    } else {
        emit('updateFormData', { field: 'genericDestination', inputValue: null })
        emit('updateFormData', { field: 'beneficiaryName', inputValue: null })
    }
    emit('updateFormData', { field: 'sendToSameHolder', inputValue: event })
}

const destinationMandatoryList = ref([
    'genericDestination',
    'beneficiaryName',
    'address'
])

const fieldsWithErrorMessage = ref({
    genericDestination: genericDestinationParams.value,
    beneficiaryName: beneficiaryNameInputParams.value,
    address: addressInputParams.value
})

const showInputsErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, destinationMandatoryList.value)

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            fieldsWithErrorMessage.value[fieldName].errorMessage = t('mandatoryFieldError')
            fieldsWithErrorMessage.value[fieldName].errorState = true
        })

        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))
    }

    if (props.formData.beneficiaryName.length && props.formData.beneficiaryName.length < 5) {
        beneficiaryNameInputParams.value.errorState = true
        beneficiaryNameInputParams.value.errorMessage = t('transfers.nacional.step1.invalidBeneficiaryName')
        emit('scrollTo', getElementOffsetTopPosition('beneficiaryName'))
    }

    const sameBankDestination = props.formData.genericDestination.includes('AO060040')

    if (props.tabsList[1].selected && sameBankDestination && props.formData.genericDestination.length > 0) {
            genericDestinationParams.value.errorMessage = t('transfers.nacional.step1.invalidIbanErrorMessage')
            genericDestinationParams.value.errorState = true
            sameBankDestinationError.value = true
            emit('scrollTo', getElementOffsetTopPosition('genericDestination'))
    }
}

watch(() => props.showInputsErrors, newVal => {
	if (newVal) showInputsErrorsHandler()
	emit('resetShowInputsErrors')
})

watch(() => props.beneficiaryInfoError.errorState, newVal => {
    if (newVal) {
        genericDestinationParams.value.errorMessage = props.beneficiaryInfoError.errorMessage
        genericDestinationParams.value.errorState = props.beneficiaryInfoError.errorState

        emit('scrollTo', getElementOffsetTopPosition('genericDestination'))
    }
})

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/

const clearFieldError = (fieldName) => {
    fieldsWithErrorMessage.value[fieldName].errorMessage = null
    fieldsWithErrorMessage.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in fieldsWithErrorMessage.value) {
		clearFieldError(fieldName)
	}
}

watch(() => props.clearForm, () => {
	clearFormErrors()
	emit('formIsCleared')
})
</script>

<style lang="scss" scoped>
.section-wrapper {
    width: 100%;

    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
    gap: 24px;

    .generic-destination-container {
        width: 100%;
        min-height: 88px;

        display: flex;
        justify-content: center;
        align-items: center;
        gap: 16px;

        &.mobyle-styles {
            flex-direction: column-reverse;

            .reuse-beneficiary-button {
                align-self: center;
                width: 100%;
                max-width: unset
            }
        }

        .generic-destination-wrapper {
            width: 100%;

            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;

            .qr-code-button {
                width: 40px;
                height: 40px;
            }
        }

        .reuse-beneficiary-button {
            width: 100%;
            max-width: 185px;
        }
    }

    .na-hora-inputs {
        width: 100%;

        display: flex;
        justify-content: center;
        align-items: center;
        gap: 24px;
    }
    .destination-wrapper {
        width: 100%;

        display: flex;
        justify-content: center;
        flex-direction: column;
        gap: 24px;

        margin-top: 24px;
    }
}
</style>
